import { Injectable } from '@angular/core';
import { Resolve, Router } from '@angular/router';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { CRMAdminAccessData } from 'src/lib/fliko/models/users/access.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { DirectDbAccessService } from 'src/app/shared/services/direct-database-access.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ContextDataService } from './context.data-service';
import { ContextService } from './context.service';
import { ContextData, ContextType } from './models/context.data';

@Injectable()
export class ContextResolver implements Resolve<ContextData> {
  constructor(
    private dbSrv: DatabaseService,
    private authService: AuthService,
    private router: Router,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    private ctxSrv: ContextService,
    private localStorageSrv: LocalStorageService,
    private errorHandling: ErrorHandlingService
  ) {}

  private readonly contextDef = { type: ContextType.ESTATE, companyIds: [], estates: [], hasCompanyAccess: false };

  async resolve(): Promise<ContextData> {
    const currentUserId = this.authService.getCurrentUserUid();
    const userId = currentUserId ?? (await this.authService.getCurrentUserUidOnce());
    if (!userId) return this.contextDef;

    let commonDbRef = DatabaseService.getCommonDbRef(this.dbSrv);
    if (!commonDbRef) {
      this.ctxSrv.setUser(userId);
      commonDbRef = DatabaseService.getCommonDbRef(this.dbSrv);
    }
    if (!commonDbRef) return this.contextDef;

    const [estates, crmAccess, superadmin] = await Promise.all([
      DirectDbAccessService.onceList<EstateData>('estate', commonDbRef),
      DirectDbAccessService.onceObject<CRMAdminAccessData>(`crmAdminAccess/${userId}`, commonDbRef),
      DirectDbAccessService.onceObject<CRMAdminAccessData>(`superadmin/${userId}`, commonDbRef)
    ]);

    const estateIds = Object.entries(crmAccess?.estates ?? {})
      .filter(([, allowed]) => allowed === true)
      .map(([estateId]) => estateId);
    const availableEstates = estates.filter((estate) => estateIds.includes(estate.id) && (superadmin || !estate.hidden));

    // ? If user has company permissions collect company ids
    const companyIds = ObjHlp.keys(crmAccess?.companies)
      .map((companyId) => companyId.toString())
      .filter((companyId) => !!crmAccess?.companies?.[companyId]);

    if (availableEstates.length === 0 && companyIds.length === 0) {
      this.errorHandling.showGlobalError(this.ls.translate('context.noEstates'));
      this.authService.logOut();
      this.router.navigate(['/signIn']);
      return this.contextDef;
    }

    const context = crmAccess?.defaultCompanyCtx ? ContextType.COMPANY : ContextType.ESTATE;
    let ctxType = <ContextType>(this.ctxDS.peekContext() ?? this.localStorageSrv.userContext.get(userId) ?? context);
    if (ctxType === ContextType.COMPANY && companyIds.length === 0) ctxType = ContextType.ESTATE;
    if (ctxType === ContextType.ESTATE && availableEstates.length === 0) ctxType = ContextType.COMPANY;

    this.ctxSrv.setContext(ctxType);
    if (ctxType === ContextType.COMPANY) {
      const currentCompanyId = this.ctxDS.peekCurrentCompanyId();
      const preferredCompanyId = currentCompanyId || this.localStorageSrv.selectedCompany.get(userId);
      const companyId = preferredCompanyId && companyIds.includes(preferredCompanyId) ? preferredCompanyId : companyIds[0];
      if (companyId && companyId !== currentCompanyId) await this.ctxSrv.setCompany(companyId);
    } else {
      const currentEstateId = this.ctxDS.peekCurrentEstateId();
      const preferredEstateId = currentEstateId || this.localStorageSrv.selectedEstate.get(userId);
      const estateId = availableEstates.find((estate) => estate.id === preferredEstateId)?.id ?? availableEstates[0].id;
      await this.ctxSrv.setEstate(estateId);
    }
    return { companyIds, hasCompanyAccess: !!companyIds.length, estates: availableEstates, type: ctxType };
  }
}
